import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { supabase } from "@/integrations/supabase/client";
import { VERSAO_ATUAL } from "@/lib/versao";

const SESSION_KEY = "corisco-sessao-navegacao";
const ID_SEGMENT = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/** "/vagas/2feb…/editar" → "/vagas/:id/editar". Nunca grava id de registro. */
export function routePattern(pathname: string): string {
  const pattern = pathname
    .split("/")
    .map((segment) => (ID_SEGMENT.test(segment) ? ":id" : segment))
    .join("/");
  return pattern.length > 1 ? pattern.replace(/\/$/, "") : pattern;
}

function sessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = Math.random().toString(36).slice(2, 12);
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "sem-storage";
  }
}

/**
 * Telemetria de navegação (sql/201): uma linha por tela, aba ou painel aberto,
 * com o padrão da rota e a tela anterior. Serve para decidir disposição de
 * telas pelo uso real. Falha em silêncio: telemetria nunca atrapalha o uso.
 */
export function useTrackPageView() {
  const location = useLocation();
  const previous = useRef<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pattern = routePattern(location.pathname);
    const aba = params.get("aba") ?? params.get("tab");
    const painel = params.get("painel") ?? params.get("etapa");
    const key = `${pattern}|${aba ?? ""}|${painel ?? ""}`;
    if (key === previous.current) return;
    const previousPattern = previous.current?.split("|")[0] ?? null;
    const referrer = previousPattern === pattern ? null : previousPattern;
    previous.current = key;

    const timer = setTimeout(() => {
      // registrar_page_view ainda não está no types.ts gerado.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      void (supabase.rpc as any)("registrar_page_view", {
        p_route_pattern: pattern,
        p_aba: aba,
        p_painel: painel,
        p_session_id: sessionId(),
        p_referrer_route: referrer,
        p_versao: VERSAO_ATUAL,
      }).then(() => undefined, () => undefined);
    }, 800);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);
}
